import { clsx } from '@alexis/helpers/clsx';
import { translateNumber, translateText } from '@alexis/helpers/translation';
import NewSegmentView from '@pages/FlightSearchResult/components/NewLegView/NewSegmentView';
import { useFlightPLS } from '@pages/FlightSearchResult/contexts/FlightPerLegSearchContext/FlightPerLegSearchContext';
import useFlightSearchFilters from '@pages/FlightSearchResult/hooks/useFlightSearchFilters';
import useFlightSearchPageParams from '@pages/FlightSearchResult/hooks/useFlightSearchPageParams';
import { GenzoTrackApplyFilterActionEventType } from '@pages/FlightSearchResult/types';
import uniqBy from 'lodash/uniqBy';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { CLOUDINARY_DAILY_KEY } from '@constants/cloudinary';

import { getImageTransformationsUrl } from '@helpers/imageTransformations';
import {
  removePrefixZeroFromTimeString,
  convertDurationInMinutesToHoursAndMinutes,
} from '@helpers/utils';

import MoonIcon from '@icons/moon_outlined.svg';
import MultiAirlineIcon from '@icons/multi-flight.svg';
import TimeIcon from '@icons/time.svg';
import WarningIcon from '@icons/warning.svg';

import { getLocaleState, getTranslationsState } from '@redux/selectors';

import type { LegViewModel } from '@wegoTypes/flights/legViewModel';

import styles from './NewLegView.module.scss';

export interface NewLegViewProps {
  isOfferEngine?: boolean;
  isFareCache?: boolean;
  hasVirtualInterline?: boolean;
  legViewModel: LegViewModel;
  genzoTrackApplyFilterSearchView: () => void;
  genzoTrackApplyFilterActionEvent: GenzoTrackApplyFilterActionEventType;
}

const NewLegView: React.FC<NewLegViewProps> = ({
  isOfferEngine,
  isFareCache,
  hasVirtualInterline,
  legViewModel,
  genzoTrackApplyFilterSearchView,
  genzoTrackApplyFilterActionEvent,
}) => {
  const { isMultiCity, isRoundTrip } = useFlightSearchPageParams();
  const translations = useSelector(getTranslationsState);
  const locale = useSelector(getLocaleState);

  const { handleOriginAirportCodeClick, handleDestinationAirportCodeClick } =
    useFlightSearchFilters();

  const { isPLSDepartLegSelection } = useFlightPLS();

  const allowFilterByAirportCode = !isMultiCity;

  const handleOnAirportCodeClick = (
    e: React.MouseEvent<HTMLDivElement>,
    searchParam: 'origin' | 'destination',
    airportCode: string,
  ): void => {
    if (allowFilterByAirportCode) {
      e.stopPropagation();

      if (searchParam === 'origin') {
        handleOriginAirportCodeClick(
          airportCode,
          genzoTrackApplyFilterSearchView,
          genzoTrackApplyFilterActionEvent,
        );
      } else {
        handleDestinationAirportCodeClick(
          airportCode,
          genzoTrackApplyFilterSearchView,
          genzoTrackApplyFilterActionEvent,
        );
      }
    }
  };

  const legViewAirlines = useMemo(() => {
    return uniqBy(legViewModel.airlines, 'name');
  }, [legViewModel.airlines]);

  const StopSection = useMemo(() => {
    if (legViewModel.segmentViewModels.length === 0) {
      return <div className={styles.direct}>{translations.direct}</div>;
    }

    if (legViewModel.segmentViewModels.length === 1) {
      const segmentViewModel = legViewModel.segmentViewModels[0];
      const stopOverTimeText = convertDurationInMinutesToHoursAndMinutes(
        segmentViewModel.segment.stopoverDurationMinutes,
      );

      return (
        <div className={clsx(styles.withStops, styles.direct)}>
          {translateText(
            translations.duration_in_airport,
            locale,
            stopOverTimeText,
            segmentViewModel.segment.arrivalAirportCode,
          )}
        </div>
      );
    }

    if (legViewModel.segmentViewModels.length === 2 || legViewModel.segmentViewModels.length === 3)
      return (
        <div className={clsx(styles.withStops, styles.direct)}>
          {translateText(
            translations['n_stops_in_d'],
            locale,
            legViewModel.segmentViewModels.length,
            legViewModel.segmentViewModels
              .map((segment) => segment.segment.arrivalAirportCode)
              .join(', '),
          )}
        </div>
      );

    return (
      <div className={clsx(styles.withStops, styles.direct)}>
        {legViewModel.segmentViewModels
          .slice(0, 2)
          .map((segment) => segment.segment.arrivalAirportCode)
          .join(', ')}
        , +{legViewModel.segmentViewModels.length - 2}
      </div>
    );
  }, [legViewModel.segmentViewModels, locale, translations]);

  const AirlinesNames = useMemo(() => {
    if (legViewAirlines.length > 3) {
      return `${legViewAirlines
        .slice(0, 2)
        .map((airline) => airline.name)
        .join(', ')}, ${translateText(
        translations.number_of_more_airline,
        locale,
        legViewAirlines.length - 2,
      )}`;
    }

    return legViewAirlines.map((airline) => airline.name).join(', ');
  }, [legViewAirlines, locale, translations.number_of_more_airline]);

  const SegmentViewSection = useMemo(() => {
    if (legViewModel.segmentViewModels.length >= 4) {
      return (
        <>
          {legViewModel.segmentViewModels.slice(0, 2).map((segmentViewModel, index) => {
            return <NewSegmentView key={index} segmentViewModels={[segmentViewModel]} />;
          })}

          <NewSegmentView segmentViewModels={legViewModel.segmentViewModels.slice(2)} />
        </>
      );
    }

    return legViewModel.segmentViewModels.map((segmentViewModel, index) => {
      return <NewSegmentView key={index} segmentViewModels={[segmentViewModel]} />;
    });
  }, [legViewModel.segmentViewModels]);

  return (
    <div
      data-pw='tripCard_legDetails'
      key={legViewModel.leg.id}
      className={clsx(
        styles.container,
        isOfferEngine && styles.isOfferEngine,
        isFareCache && styles.isFareCache,
      )}
    >
      {/* Section: Airline Information */}
      <div className={styles.airlineInfo}>
        <div data-testid='leg_view_airline_images' className={styles.images}>
          {legViewAirlines.slice(0, legViewAirlines.length > 3 ? 2 : 3).map((airline, index) => (
            <img
              key={airline.code}
              className={index === 0 ? styles.mainImage : styles.subImage}
              src={getImageTransformationsUrl(
                `https://assets.wego.com/image/upload/${CLOUDINARY_DAILY_KEY}/flights/airlines_square/${airline?.code}.png`,
              )}
            />
          ))}

          {legViewAirlines.length > 3 ? <MultiAirlineIcon /> : null}
        </div>

        <span data-pw='leg_airlineName' className={styles.airlineName}>
          {AirlinesNames}
        </span>
      </div>

      {/* Section: Time information */}
      <div className={styles.timeInformation}>
        <div
          id='flightCardLegDepartureTime'
          data-pw='leg_scheduleInfo'
          className={styles.timeAndAirportCode}
        >
          <div className={styles.time}>{legViewModel.leg.departureTime}</div>
          <div className={styles.tooltipContainer}>
            <div
              data-pw='leg_departureAirportCode'
              className={clsx(
                styles.airportCode,
                allowFilterByAirportCode && styles.filterByAirportCode,
              )}
              onClick={(e) =>
                handleOnAirportCodeClick(
                  e,
                  isRoundTrip && !isPLSDepartLegSelection ? 'destination' : 'origin',
                  legViewModel.leg.departureAirportCode,
                )
              }
            >
              {legViewModel.leg!.departureAirportCode}
            </div>

            <div className={clsx(styles.tooltip, styles.bottom)}>
              {!isMultiCity ? translations.show_only_flights : legViewModel.departureAirport?.name}
            </div>
          </div>
        </div>

        <div className={styles.timeLine}>
          <div className={styles.start}>
            <div className={styles.point}></div>
          </div>

          {SegmentViewSection}

          <div className={styles.end}>
            <div className={styles.point}></div>
          </div>
        </div>

        <div data-pw='leg_scheduleInfo' className={styles.timeAndAirportCode}>
          <div className={styles.time}>{legViewModel.leg.arrivalTime}</div>
          <div className={styles.tooltipContainer}>
            <div
              data-pw='leg_arrivalAirportCode'
              className={clsx(
                styles.airportCode,
                allowFilterByAirportCode && styles.filterByAirportCode,
              )}
              onClick={(e) =>
                handleOnAirportCodeClick(
                  e,
                  isRoundTrip && !isPLSDepartLegSelection ? 'origin' : 'destination',
                  legViewModel.leg.arrivalAirportCode,
                )
              }
            >
              {legViewModel.leg.arrivalAirportCode}
            </div>

            <div className={clsx(styles.tooltip, styles.bottom)}>
              {!isMultiCity ? translations.show_only_flights : legViewModel.arrivalAirport?.name}
            </div>
          </div>
        </div>

        <div data-pw='leg_overNightFlightAndNoOfDaysInfo' className={styles.overnight}>
          {legViewModel.leg.overnight ? (
            <>
              <div className={styles.tooltipContainer}>
                <div className={styles.nightCount}>
                  {legViewModel.leg.durationDays > 0 ? '+' : ''}
                  {translateNumber(legViewModel.leg.durationDays, locale === 'fa')}
                </div>

                <div className={clsx(styles.tooltip, styles.bottom)}>
                  {translateText(
                    translations.flight_land,
                    locale,
                    legViewModel.leg.durationDays === 1 ? 0 : legViewModel.leg.durationDays,
                  )}
                </div>
              </div>

              <div className={styles.tooltipContainer}>
                <MoonIcon className={clsx(styles.icon, styles.moonIcon)} />

                <div className={clsx(styles.tooltip, styles.bottom)}>
                  {translations.overnight_flight}
                </div>
              </div>
            </>
          ) : null}
          {hasVirtualInterline && (
            <div className={styles.tooltipContainer}>
              <WarningIcon className={clsx(styles.icon, styles.warningIcon)} />

              <div className={clsx(styles.tooltip, styles.bottom, styles.selfTransferTooltip)}>
                {translations.flight_result_self_transfer_tooltip}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Section: Duration and stop */}
      <div id='flightCardDurationAndStop' className={styles.durationAndStop}>
        <div className={styles.totalDuration}>
          <TimeIcon className={styles.icon} />

          {removePrefixZeroFromTimeString(legViewModel.leg.duration)}
        </div>
        {StopSection}
      </div>
    </div>
  );
};

export default NewLegView;
